import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useLayout } from '@/contexts/LayoutContext';

interface StickyBackBarProps {
    destinationLabel: string;
    backPath: string;
    children?: ReactNode;
}

/**
 * Generic sticky back bar for navigation.
 * Displays "Back to {destinationLabel}" on the left.
 * Optional children are rendered on the right (e.g., action buttons).
 * Sticks below the header when scrolling.
 */
export function StickyBackBar({
    destinationLabel,
    backPath,
    children,
}: StickyBackBarProps) {
    const navigate = useNavigate();
    const { isFullWidth } = useLayout();

    const handleBack = () => {
        navigate(backPath);
    };

    return (
        <div
            className="
                sticky top-[57px]
                z-20 relative
                -mt-2 md:-mt-2 lg:-mt-3
            "
        >
            {/* Background that extends to fill main content area */}
            <div className="absolute inset-0 left-[-100vw] right-[-100vw] bg-white dark:bg-darker border-b border-gray-200 dark:border-gray-700" />
            <div className="relative">
                <div className={`py-3 flex items-center justify-between relative h-12 ${isFullWidth ? '' : 'max-w-(--breakpoint-2xl)'}`}>
                    {/* Back button - left side */}
                    <button
                        onClick={handleBack}
                        className="flex items-center gap-2 -ml-1 px-1 py-1 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-800 transition-colors"
                        aria-label={`Go back to ${destinationLabel}`}
                    >
                        <ArrowLeftIcon className="h-6 w-6 shrink-0" />
                        <span className="text-base font-medium">
                            Back to {destinationLabel}
                        </span>
                    </button>

                    {/* Optional children - right side */}
                    {children && (
                        <div className="relative my-1.5">
                            {children}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
